import {
  Loading,
  openExternalLink,
  PDFViewer,
  Typography,
  useFetchFile,
  UserTypeEnum,
} from "@cpj-ai/panel-npm-package";
import { Button, Modal, ModalBody, ModalContent, ModalHeader, useDisclosure } from "@heroui/react";
import { usePostHiringStore } from "@store/usePostHiringStore";
import dayjs from "dayjs";
import { useCallback, useEffect } from "react";
import toast from "react-hot-toast";
import CertificateStatusBadge from "../CertificateStatusBadge";

const CertificateDetailModal = () => {
  const { selectedCertificate, setSelectedCertificate } = usePostHiringStore();
  const { isOpen, onClose, onOpen, onOpenChange } = useDisclosure();
  const { data, loading } = useFetchFile({
    urlPath: selectedCertificate?.file,
    cache: "no-cache",
    userType: UserTypeEnum.ORG,
  });

  useEffect(() => {
    if (selectedCertificate?.title) {
      onOpen();
    } else {
      onClose();
    }
  }, [onClose, onOpen, selectedCertificate?.title]);

  const handleClose = useCallback(() => {
    onClose();
    setSelectedCertificate(null);
  }, [onClose, setSelectedCertificate]);

  const getFileExtension = (filename?: string) => {
    if (!filename) return "";
    return filename.split(".").pop()?.toLowerCase() ?? "";
  };

  const handleViewCertificate = useCallback(() => {
    if (selectedCertificate?.link) {
      openExternalLink({ url: selectedCertificate?.link });
    } else {
      toast.error("Certificate link is not available");
    }
  }, [selectedCertificate?.link]);

  const renderContent = useCallback(() => {
    if (selectedCertificate?.link) {
      return (
        <Button onPress={handleViewCertificate} className="w-1/2 bg-[#F5A524] text-white block mx-auto mt-8">
          View Certificate
        </Button>
      );
    }

    if (!selectedCertificate?.file || (!data && !loading)) {
      return <Typography className="text-center">No file available</Typography>;
    }

    const extension = getFileExtension(selectedCertificate?.file);
    const imageExtensions = ["jpg", "jpeg", "png", "gif", "webp"];

    if (imageExtensions.includes(extension)) {
      return (
        <>
          {!loading ? (
            <img src={data} alt={selectedCertificate?.title ?? ""} className="max-w-full object-contain" />
          ) : (
            <Loading />
          )}
        </>
      );
    }

    if (extension === "pdf") {
      if (loading) return <Loading />;
      return <PDFViewer url={data} className="h-[50vh] w-full" />;
    }

    return <Typography className="text-center text-red-500">This file format is not supported</Typography>;
  }, [
    data,
    handleViewCertificate,
    loading,
    selectedCertificate?.file,
    selectedCertificate?.link,
    selectedCertificate?.title,
  ]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} onOpenChange={onOpenChange} size="5xl">
      <ModalContent className="p-4">
        <ModalHeader className="capitalize">{selectedCertificate?.title}</ModalHeader>
        <ModalBody>
          <div className="flex items-center gap-2">
            <Typography className="capitalize">{selectedCertificate?.issuer}</Typography>
            <span className="text-zinc-700">•</span>
            <Typography className="text-zinc-700">
              {`${
                selectedCertificate?.date?.from ? dayjs(selectedCertificate.date?.from).format("MMM YYYY") : "Current"
              } - ${
                selectedCertificate?.date?.to ? dayjs(selectedCertificate?.date?.to).format("MMM YYYY") : "Current"
              }`}
            </Typography>
          </div>
          <CertificateStatusBadge status={selectedCertificate?.status} />
          <div className="max-h-[60vh] overflow-y-auto mt-4">{renderContent()}</div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CertificateDetailModal;
